import type { Batch } from '@ctcells/contract'
import { t } from '@ctcells/i18n'
import { useRef, useState } from 'react'

import { Button } from '../atoms/Button.tsx'
import { Icon } from '../atoms/Icon.tsx'
import { Modal } from '../atoms/Modal.tsx'
import { Field } from '../atoms/Field.tsx'
import { useSubmitValidation } from '../atoms/useSubmitValidation.ts'
import { TextInput } from '../atoms/TextInput.tsx'
import { ValueRows } from '../atoms/ValueRows.tsx'
import { gate, type ActionGate, type ScreenAccess } from '../screens/access.ts'
import {
  reagentDrafts,
  reagentPlan,
  type ReagentBody,
  type ReagentLineDraft,
} from './reagentConfirm.ts'

// `.vissue`(실패 문구 한 줄)는 `resource.css` · `.pfblk__*`(점검 목록 꼴)는 `screens.css` 에 산다
import '../screens/resource.css'
import '../screens/screens.css'
import './stripReagent.css'

export interface StripReagentDialogProps {
  /** 대상 작업 — 표시용 */
  batchId: string
  /** 그 배치 레시피 판의 표준 충전량(`Batch.reagent_basis`) — 칸의 기본값 */
  basis: Batch['reagent_basis']
  /** 보낼 염색 모듈(`Batch.reagent_module_id`) — `null` 이면 «대상 미정» · 확정 비활성 */
  moduleId: string | null
  access?: ScreenAccess
  /** 보충 기록을 보내는 중 · 그 뒤 사전 점검이 도는 중 */
  pending?: boolean | undefined
  /** 거절 문구 — 앞서 보낸 기록은 되돌리지 않는다(사실 기록) */
  errorMessage?: string | undefined
  onCancel: () => void
  onConfirm: (bodies: ReagentBody[]) => void
}

/**
 * **시작 전 시약 용량 확인 창**(정본 `07` §4.4 ③ · 모델은 `reagentConfirm.ts`).
 *
 * 🔴 칸의 기본값은 **레시피 표준 충전량**이다 — 잰 값이 아니다. 「기준」 줄이 그 사실을 말한다.
 * 🔴 대상 모듈은 서버 칸(`reagent_module_id`) 하나다. 없으면 «대상 미정» + 까닭(서버 설명 문면) · 칸은 읽기 전용 · [확인하고 시작]은
 *    비활성 + 사유 — 시작 막힘의 판정은 서버 사전 점검(`STAINING_TARGET_AMBIGUOUS` 등)이 한다.
 * 🔴 [확인하고 시작] 활성 = control 권한 ∧ 제어락 ∧ 대상 모듈 있음 ∧ 칸 전부 유효 — 그 밖의 판정을 하지 않는다.
 * ⚠ 겉모습은 폼 창(`variant: 'form'` · 불투명 바탕) — 사람이 값을 적는 창이라 「추가」 창과 같은 읽기 위주의 꼴을 쓴다.
 *   바닥 줄 버튼이 필요해 `AddModal` 대신 `Modal` 에 같은 겉모습을 직접 건다(작업 줄 대화들은 셸 밖이라 `scope` 는 화면 전체).
 */
export function StripReagentDialog({
  batchId,
  basis,
  moduleId,
  access = {},
  pending,
  errorMessage,
  onCancel,
  onConfirm,
}: StripReagentDialogProps) {
  const [lines, setLines] = useState<ReagentLineDraft[]>(() => reagentDrafts(basis))
  const root = useRef<HTMLDivElement>(null)
  const validation = useSubmitValidation<ReagentLineDraft[], string>((value) =>
    Object.fromEntries(
      reagentPlan(value).invalid.map(({ line, well }) => [
        `${String(line)}|${well}`,
        t('reason.reagentVolumeInvalid'),
      ]),
    ),
  )
  const plan = reagentPlan(lines)
  const readOnly = moduleId === null

  const confirmGate: ActionGate = pending
    ? { disabled: true, disabledReason: t('reason.actionInFlight') }
    : gate(access, {
        perm: 'control',
        needsLock: true,
        stateOk: moduleId !== null,
        stateReason: 'reason.reagentTargetUnknown',
      })

  const setText = (lineIndex: number, wellIndex: number, text: string) => {
    const next = lines.map((line, i) =>
      i !== lineIndex
        ? line
        : {
            ...line,
            wells: line.wells.map((cell, j) => (j === wellIndex ? { ...cell, text } : cell)),
          },
    )
    setLines(next)
    const well = lines[lineIndex]?.wells[wellIndex]?.well
    if (well !== undefined) validation.change(`${String(lineIndex)}|${well}`, next)
  }

  return (
    <Modal
      busy={pending === true}
      footer={
        <>
          <Button onClick={onCancel} size="pill">
            {t('action.cancel')}
          </Button>
          <Button
            onClick={() => {
              if (confirmGate.disabled || !validation.validate(lines, root.current)) return
              onConfirm(plan.bodies)
            }}
            size="pill"
            variant="primary"
            {...confirmGate}
          >
            <Icon name="play" size="sm" />
            {t('action.confirmAndStart')}
          </Button>
        </>
      }
      onClose={onCancel}
      size="lg"
      title={t('strip.reagentTitle')}
      variant="form"
    >
      <ValueRows
        rows={[
          { key: 'batch', label: t('column.batch'), value: batchId },
          {
            key: 'module',
            label: t('strip.reagentModule'),
            value: moduleId ?? t('strip.reagentTargetUnknown'),
          },
          { key: 'basis', label: t('strip.reagentBasis'), value: t('strip.reagentBasisValue') },
        ]}
      />

      <div ref={root}>
        {lines.map((line, lineIndex) =>
          line.labwareId === null ? null : (
            <section
              aria-label={line.reagent}
              className="rgconf__line"
              key={`${String(lineIndex)}:${line.reagent}`}
            >
              <h3 className="rgconf__name">
                {line.reagent}
                <span className="rgconf__labware mono">{line.labwareId}</span>
              </h3>
              <div className="rgconf__wells">
                {line.wells.map((cell, wellIndex) => (
                  <div className="rgconf__well" key={cell.well}>
                    <Field
                      label={cell.well}
                      {...validation.field(`${String(lineIndex)}|${cell.well}`).fieldProps}
                    >
                      <TextInput
                        aria-label={t('strip.reagentWell', {
                          reagent: line.reagent,
                          well: cell.well,
                        })}
                        inputMode="decimal"
                        {...validation.field(`${String(lineIndex)}|${cell.well}`).inputProps}
                        onChange={(text) => {
                          setText(lineIndex, wellIndex, text)
                        }}
                        readOnly={readOnly}
                        value={cell.text}
                      />
                      <span className="rgconf__unit">µL</span>
                    </Field>
                  </div>
                ))}
              </div>
            </section>
          ),
        )}
      </div>
      {plan.unplaced.map((reagent) => (
        <p className="pfblk__action" key={reagent} role="status">
          {t('strip.reagentUnplaced', { reagent })}
        </p>
      ))}

      {errorMessage === undefined ? null : (
        <p className="vissue" role="alert">
          {errorMessage}
        </p>
      )}
    </Modal>
  )
}
